import RichText from 'components/UI/Richtext';
import { ReactElement, useContext } from 'react';
import {
  IComponentVideoPlayerFields,
  IFragmentContentfulHostedVideo,
  IFragmentRendiaVideo,
  IFragmentVimeoVideo,
  IFragmentYouTubeVideo,
  IFragmentFreshpaintVideo,
} from 'types/contentful';
import { useRouter } from 'next/router';
import SiteDataContext from 'context/SiteDataContext';
import Vimeo, { isVimeoVideoGuard } from 'components/UI/Videos/Vimeo';
import FreshpaintVideo, { isFreshpaintVideoGuard } from 'components/UI/Videos/Freshpaint';
import YouTube, { isYoutubeVideoGuard } from 'components/UI/Videos/Youtube';
import Rendia, { isRendiaVideoGuard } from 'components/UI/Videos/Rendia';
import ContentfulVideo from 'components/UI/Videos/ContentfulVideo';

export enum VideoTypeId {
  Vimeo = 'fragmentVimeoVideo',
  Youtube = 'fragmentYouTubeVideo',
  Contentful = 'fragmentContentfulHostedVideo',
  Rendia = 'fragmentRendiaVideo',
  Freshpaint = 'fragmentFreshpaintVideo',
}

export type VideoTypes =
  | IFragmentContentfulHostedVideo
  | IFragmentVimeoVideo
  | IFragmentYouTubeVideo
  | IFragmentRendiaVideo
  | IFragmentFreshpaintVideo;

function fullUrl(domain: string, path: string): URL {
  const protocol = domain.startsWith('localhost') ? 'http' : 'https';

  return new URL(`${protocol}://${domain}${path}`);
}

interface VideoPlayerProps extends IComponentVideoPlayerFields {
  className?: string | undefined;
}

export default function VideoPlayer({
  heading,
  video,
  backgroundColor,
  description,
  className,
}: VideoPlayerProps): ReactElement {
  const router = useRouter();
  const { domain } = useContext(SiteDataContext);

  const Comp = (): ReactElement => {
    if (isVimeoVideoGuard(video)) {
      return <Vimeo video={video} />;
    } else if (isYoutubeVideoGuard(video)) {
      return <YouTube video={video} />;
    } else if (isRendiaVideoGuard(video)) {
      return <Rendia video={video} url={fullUrl(domain, router.asPath)} />;
    } else if (isFreshpaintVideoGuard(video)) {
      return <FreshpaintVideo video={video} />;
    } else {
      return <ContentfulVideo video={video} />;
    }
  };

  return (
    <div className="mb-6">
      <div className={` bg-transparent text-center pt-6 lg:pt-12`}>
        {heading && <h2 className="font-heading text-2xl lg:text-4xl font-extrabold">{heading}</h2>}
        {description && (
          <div className="px-4 lg:px-32 mt-4 text-left">
            <RichText document={description} />
          </div>
        )}
      </div>
      <div className={`bg-${backgroundColor} pb-4 lg:pb-16 text-center mt-20`}>
        <div className={`inline-block -mt-4 lg:-mt-16 px-4 w-full ${className ? '' : 'lg:w-3/4'}`}>
          <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
            <Comp />
          </div>
        </div>
      </div>
    </div>
  );
}
